import { Injectable, Optional } from '@angular/core';
import { LoggerService } from 'src/app/services/logger.service';
import {
  FormulaJSON,
  FormulaVariable,
  slugifyFieldKey,
  normalizeRollupMode,
  UNIT_PRICE_TOKEN,
  isPercentOfUnitPriceAction,
  clampDiscountPercent,
  formatFormulaDisplayNumber,
} from './formula.types';
import { FormulaEngineService } from './formula-engine.service';

@Injectable({ providedIn: 'root' })
export class FormulaContextService {
  private static readonly RESERVED_CONTEXT_KEYS = new Set([
    'id', 'quantity', 'qty', 'price', 'total', 'discount',
    'discprice', 'item_name', 'item_code', 'item_image', 'description', 'unit',
    'peritemprice', 'peritemdisc', 'peritemtax', 'tax_price', 'total_discount',
    'priceafterdiscount', 'priceaftertax', 'profitmargin', 'inventory',
    'item_type', 'minprice', 'newaddedprice', 'initialquantity', 'formularesults',
    'itemscustomfields', 'items_custom_field_datas', 'item_prices', 'fulldata',
    'picture', 'taxes', 'comments', 'category', 'subcategory',
    'itemtotal',
  ]);

  constructor(
    private readonly engine: FormulaEngineService,
    @Optional() private readonly logger?: LoggerService,
  ) {}

  /** A price tier: a finite number, or undefined (missing / unparseable) so "Unknown variable" applies. */
  private static priceTier(value: any): number | undefined {
    if (value === undefined || value === null || String(value).trim() === '') {
      return undefined;
    }
    const n = parseFloat(String(value).replace(/,/g, '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : undefined;
  }

  static parseNumeric(value: any): number {
    if (value === undefined || value === null || value === '') {
      return 0;
    }
    const n = parseFloat(String(value).replace(/,/g, '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
  }

  /**
   * Discount percent from the shapes the app stores: "10%", "10", 10, ["10%"] (eQuote item rows),
   * "5,10,15" (an item's list of offered discounts — the first one, as the quote mappers use) or
   * [{ discounts: "10" }] (the item editor's list). Unknown / empty → 0.
   */
  static parseDiscount(raw: any): number {
    let value = Array.isArray(raw) ? raw[0] : raw;
    if (value && typeof value === 'object') {
      value = value.discounts ?? value.discount ?? value.value;
    }
    if (value === undefined || value === null || value === '') {
      return 0;
    }
    return FormulaContextService.parseNumeric(String(value).split(',')[0]);
  }

  /**
   * Price A–D from the price JSON. A malformed JSON or a missing / non-numeric tier is UNDEFINED,
   * never 0: a price-setting formula reading it then errors ("Unknown variable") instead of writing $0.
   */
  parsePriceObject(priceRaw: any): Record<string, number | undefined> {
    let price = priceRaw;
    if (typeof price === 'string') {
      try {
        price = JSON.parse(price);
      } catch (err: any) {
        this.logger?.log('formula:price JSON unreadable', err?.message || 'invalid JSON');
        price = {};
      }
    }
    const tier = (v: any) => FormulaContextService.priceTier(v);
    return {
      pricesA: tier(price?.pricesA),
      pricesB: tier(price?.pricesB),
      pricesC: tier(price?.pricesC),
      pricesD: tier(price?.pricesD),
    };
  }

  /**
   * @param flatFieldNames the merchant's real custom-field names (from the loaded field list). Only
   *   those are read from flat keys on the line; without it no flat key is read. Harvesting every
   *   numeric scalar put cost, commission and totals (fix_cost_per_unit, totalTaxItem…) in the context.
   */
  buildContext(lineItem: any, quantityOverride?: number, flatFieldNames?: ReadonlySet<string>): Record<string, number> {
    const quantity = FormulaContextService.parseNumeric(
      quantityOverride ??
        lineItem?.quantity ??
        lineItem?.Quantity ??
        lineItem?.QUANTITY ??
        lineItem?.QTY ??
        lineItem?.current_quantity ??
        lineItem?.unit_quantity,
    );

    const priceObj = this.resolvePriceTiers(lineItem);
    const rawUnitPrice =
      lineItem?.perItemPrice ?? lineItem?.newAddedPrice ?? lineItem?.Price;
    const hasExplicitUnitPrice =
      rawUnitPrice !== undefined && rawUnitPrice !== null && rawUnitPrice !== '';
    // No explicit price and no Price A: unknown, not 0 (a formula reading it errors).
    const unitPrice = hasExplicitUnitPrice
      ? FormulaContextService.parseNumeric(rawUnitPrice)
      : priceObj.pricesA;
    // The line's applied discount wins; else the catalog price row's. Number("10%") was NaN → 0.
    const discount = FormulaContextService.parseDiscount(
      lineItem?.discount ??
        lineItem?.Discount ??
        lineItem?.item_prices?.[0]?.discount ??
        lineItem?.fulldata?.item_prices?.[0]?.discount,
    );

    const context = Object.create(null) as Record<string, number>;
    context['Item.Quantity'] = quantity;
    context['Item.Unit_Price'] = unitPrice;
    context['Item.Total'] = Number.isFinite(unitPrice) ? quantity * unitPrice : undefined;
    context['Item.Price_A'] = priceObj.pricesA;
    context['Item.Price_B'] = priceObj.pricesB;
    context['Item.Price_C'] = priceObj.pricesC;
    context['Item.Price_D'] = priceObj.pricesD;
    context['Item.Discount_Percent'] = clampDiscountPercent(discount);

    const customDatas =
      lineItem?.items_custom_field_datas ||
      lineItem?.itemsCustomFields ||
      lineItem?.itemsCustomFieldData ||
      [];

    for (const cf of customDatas) {
      const name = cf?.itemsCustomField?.name || cf?.name;
      const fieldKey = cf?.itemsCustomField?.field_key || cf?.field_key || (name ? slugifyFieldKey(name) : null);
      if (!fieldKey && !name) continue;
      const attr = (fieldKey || slugifyFieldKey(name)).replace(/^custom_/, '');
      const attrId = attr
        .split('_')
        .filter(Boolean)
        .map((p: string) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('');
      const raw = cf?.value ?? cf?.displayValue;
      const n = FormulaContextService.parseNumeric(raw);
      if (Number.isFinite(n)) {
        context[`Item.Custom.${attrId}`] = n;
        if (name && !/[.[\]]/.test(String(name))) {
          context[`Item.Custom.${name}`] = n;
        }
      }
    }

    // Backend item list endpoints often flatten custom fields onto the item
    // (item.Height = "10") and delete items_custom_field_datas. Read those too.
    this.addFlatCustomFieldsToContext(lineItem, context, flatFieldNames);
    if (lineItem?.fulldata) {
      this.addFlatCustomFieldsToContext(lineItem.fulldata, context, flatFieldNames);
    }

    return context;
  }

  /** Pull Price A–D from item_prices JSON, quote line copies, or itemsComing. */
  resolvePriceTiers(lineItem: any): Record<string, number | undefined> {
    const priceObj = this.parsePriceObject(
      lineItem?.item_prices?.[0]?.price ??
        lineItem?.item_prices?.price ??
        lineItem?.fulldata?.item_prices?.[0]?.price ??
        lineItem?.fulldata?.price ??
        lineItem?.price,
    );
    const coming = Array.isArray(lineItem?.itemsComing) ? lineItem.itemsComing : [];
    const keys = ['pricesA', 'pricesB', 'pricesC', 'pricesD'];
    keys.forEach((key, index) => {
      if (!priceObj[key]) {
        const fromComing = FormulaContextService.priceTier(coming[index]?.itemPrice);
        if (fromComing !== undefined) {
          priceObj[key] = fromComing;
        }
      }
    });
    return priceObj;
  }

  private addFlatCustomFieldsToContext(
    source: any,
    context: Record<string, number>,
    allowedNames?: ReadonlySet<string>,
  ): void {
    if (!source || typeof source !== 'object') {
      return;
    }

    const reserved = FormulaContextService.RESERVED_CONTEXT_KEYS;

    const blockedKeys = new Set([
      '__proto__', 'constructor', 'prototype',
      '__defineGetter__', '__defineSetter__', '__lookupGetter__', '__lookupSetter__',
    ]);

    for (const [key, val] of Object.entries(source)) {
      if (!key || !Object.prototype.hasOwnProperty.call(source, key)) {
        continue;
      }
      if (!allowedNames?.has(key)) {
        continue; // only the merchant's real custom fields, never cost / commission / totals
      }
      if (blockedKeys.has(key) || blockedKeys.has(key.toLowerCase())) {
        continue;
      }
      const compactKey = String(key).replace(/\s+/g, '');
      if (!compactKey || blockedKeys.has(compactKey) || blockedKeys.has(compactKey.toLowerCase())) {
        continue;
      }
      if (reserved.has(key.toLowerCase()) || reserved.has(compactKey.toLowerCase()) || key.startsWith('_')) {
        continue;
      }
      if (!/^[A-Za-z][A-Za-z0-9_-]{0,49}$/.test(compactKey)) {
        continue;
      }
      if (val === null || val === undefined || val === '') {
        continue;
      }
      if (typeof val === 'object') {
        continue;
      }
      const n = parseFloat(String(val).replace(/,/g, ''));
      if (!Number.isFinite(n)) {
        continue;
      }
      // Only treat as custom numeric attribute when the whole value is numeric-ish
      if (String(val).trim() !== '' && !/^-?\d+(\.\d+)?$/.test(String(val).trim().replace(/,/g, ''))) {
        continue;
      }

      const contextKey = compactKey;
      context[`Item.Custom.${contextKey}`] = n;
      const attr = slugifyFieldKey(contextKey).replace(/^custom_/, '');
      const attrId = attr
        .split('_')
        .filter(Boolean)
        .map((p: string) => p.charAt(0).toUpperCase() + p.slice(1))
        .join('');
      if (attrId) {
        context[`Item.Custom.${attrId}`] = n;
      }
    }
  }

  evaluateFormulaFields(
    lineItem: any,
    formulaFields: any[],
    quantityOverride?: number,
    flatFieldNames?: ReadonlySet<string>,
  ): Record<string, { value: number; display: string; error?: string; warning?: string; field: any }> {
    const sorted = [...(formulaFields || [])].sort(
      (a, b) => (a.execution_order || 0) - (b.execution_order || 0),
    );
    const context = this.buildContext(lineItem, quantityOverride, flatFieldNames);
    const results: Record<string, any> = {};

    for (const field of sorted) {
      let formula: FormulaJSON = field?.formula_json;
      if (typeof formula === 'string' || !formula || typeof formula !== 'object') {
        try {
          formula = typeof formula === 'string' ? JSON.parse(formula) : null;
        } catch {
          formula = null;
        }
        if (!formula || typeof formula !== 'object') {
          // Recorded, not skipped: the line shows Error and its price / discount are left alone.
          this.logger?.log('formula:invalid formula_json', `field ${field?.id ?? field?.name ?? '?'}`);
          results[field?.field_key || field?.id || field?.name] = {
            value: 0,
            display: 'Error',
            error: 'Invalid formula JSON',
            field: { id: field?.id, name: field?.name, field_key: field?.field_key, unit_of_measure: field?.unit_of_measure, field_type_id: field?.field_type_id },
          };
          continue;
        }
      }

      const executed = this.engine.execute(formula, context);
      // The field's own unit, even an empty one; the recipe copy only when the field has none at all
      // (a cleared unit must not fall back to a stale copy).
      const unit = field.unit_of_measure ?? formula.metadata?.unit_of_measure ?? '';
      const display = executed.error
        ? 'Error'
        : `${formatFormulaDisplayNumber(executed.result)}${unit ? ' ' + unit : ''}`;

      const key = field.field_key || field.id || field.name;
      results[key] = {
        value: executed.result,
        display,
        error: executed.error,
        warning: executed.warning,
        quote_impact: executed.quote_impact,
        // Only what the line, the rollup and the templates read. The full definition carries
        // items_custom_field_datas for every item in the workspace, and formulaResults is saved
        // inside field_styles — copying it bloated every saved quote.
        field: {
          id: field.id,
          name: field.name,
          field_key: field.field_key,
          unit_of_measure: field.unit_of_measure,
          field_type_id: field.field_type_id,
          execution_order: field.execution_order,
          formula_json: formula,
          rollup_mode: normalizeRollupMode(formula.metadata?.rollup_mode ?? field?.rollup_mode),
        },
      };

      // Allow subsequent formulas to read prior passive results
      if (!executed.error && formula.metadata?.field_key) {
        const attr = formula.metadata.field_key.replace(/^custom_/, '');
        const attrId = attr
          .split('_')
          .filter(Boolean)
          .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
          .join('');
        context[`Item.Custom.${attrId}`] = executed.result;
      }
    }

    return results;
  }

  getUsedVariableIds(formula: FormulaJSON): string[] {
    const used = new Set<string>();
    const collect = (value: unknown) => {
      if (
        typeof value === 'string' &&
        (value.startsWith('Item.') || value.startsWith('Quote.'))
      ) {
        used.add(value);
      }
    };

    if (formula?.logic_mode === 'conditional') {
      collect(formula.condition?.left);
      collect(formula.condition?.right);
      collect(formula.true_action?.value);
      collect(formula.false_action?.value);
      if (
        isPercentOfUnitPriceAction(formula.true_action) ||
        isPercentOfUnitPriceAction(formula.false_action)
      ) {
        used.add(UNIT_PRICE_TOKEN);
      }
      return Array.from(used);
    }

    formula?.steps?.forEach((s) => {
      collect(s.value);
      collect(s.operand);
    });

    return Array.from(used);
  }

  labelForVariable(id: string, variables: FormulaVariable[]): string {
    return variables.find((v) => v.id === id)?.label || id;
  }
}
